import React, { InputHTMLAttributes, ReactNode } from 'react';
import classNames from 'classnames';
import Fieldset from '../Fieldset';
import DateInputField from './DateInputField';
import controlHintIds from '../controlHintIds';
import FormGroup from '../FormGroup';
import Legend from '../Legend';
import Hint from '../Hint';
import ErrorMessage from '../ErrorMessage';
import { getGlobalFormConfig, useFormConfig } from '../FormConfigContext';

export interface DateInputProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  errorMessage?: string;
  hint?: string;
  labelHint?: ReactNode;
  note?: ReactNode;
  required?: boolean;
  requiredLabel?: ReactNode;
  disabled?: boolean;
  month?: string;
  day?: string;
  year?: string;
  monthRef?: React.MutableRefObject<HTMLInputElement | null> | ((instance: HTMLInputElement | null) => void) | null;
  dayRef?: React.MutableRefObject<HTMLInputElement | null> | ((instance: HTMLInputElement | null) => void) | null;
  yearRef?: React.MutableRefObject<HTMLInputElement | null> | ((instance: HTMLInputElement | null) => void) | null;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

/**
 * @deprecated This component is deprecated.
 * The component `<MemorableDate />` should be used instead.
 *
 * The DateInput Control based on [The USWDS Date input component](https://designsystem.digital.gov/components/form-controls/#date-input)
 *
 * note: Configured `requiredLabel` text is appended to the labelhint if required attribute is true.
 */
const DateInput = ({
  id,
  className,
  label,
  labelHint,
  errorMessage,
  required,
  requiredLabel,
  disabled,
  hint,
  'aria-describedby': ariaDescribedBy,
  month,
  day,
  year,
  monthRef,
  dayRef,
  yearRef,
  onChange,
  onBlur,
  onFocus,
}: DateInputProps): JSX.Element => {
  const { labelHintId, hintId, errorId, describedBy } = controlHintIds(
    id,
    labelHint,
    hint,
    errorMessage,
    undefined,
    ariaDescribedBy,
  );
  const { requiredLabel: contextRequiredLabel } = useFormConfig();
  const { requiredLabel: globalRequiredLabel } = getGlobalFormConfig();
  const finalRequiredLabel = requiredLabel || contextRequiredLabel || globalRequiredLabel;
  return (
    <FormGroup error={errorId && errorMessage}>
      <Fieldset className={className}>
        <Legend>
          {label}
          {required ? <> {finalRequiredLabel}</> : null}
          {labelHint ? (
            <>
              {' '}
              <Hint id={labelHintId}>{labelHint}</Hint>
            </>
          ) : null}
        </Legend>
        {hint && hintId ? <Hint id={hintId}>{hint}</Hint> : null}
        {errorId && errorMessage ? <ErrorMessage id={errorId}>{errorMessage}</ErrorMessage> : null}
        <div id={id} className="usa-memorable-date">
          <DateInputField
            id={`${id}-month`}
            className="usa-memorable-date-month"
            type="month"
            label="Month"
            aria-describedby={describedBy}
            maxLength={2}
            value={month}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            ref={monthRef}
            disabled={disabled}
          />
          <DateInputField
            id={`${id}-day`}
            className="usa-memorable-date-day"
            type="day"
            label="Day"
            aria-describedby={describedBy}
            maxLength={2}
            value={day}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            ref={dayRef}
            disabled={disabled}
          />
          <DateInputField
            id={`${id}-year`}
            className="usa-memorable-date-year"
            type="year"
            label="Year"
            aria-describedby={describedBy}
            maxLength={4}
            value={year}
            onChange={onChange}
            onBlur={onBlur}
            onFocus={onFocus}
            ref={yearRef}
            disabled={disabled}
          />
        </div>
      </Fieldset>
    </FormGroup>
  );
};

export default DateInput;
